<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
  import { ActionMenu, IconImage, Tooltip } from '@/shared/ui';
  import type { ActionMenuItem } from '@/shared/ui';
  import SettingsIcon from '@/shared/assets/icons/SettingsIcon.svg';
  import HomologationIcon from '@/shared/assets/icons/VectorIcon.svg';
  import TrashIcon from '@/shared/assets/icons/RedTrashIcon.svg';

  /**
   * "Mais ações" menu of a record row. It is a thin adapter over the shared
   * `ActionMenu`: the open state belongs to the listing, so the menu is
   * controlled from outside and only one row can be open at a time, and the
   * panel is positioned in the viewport because the table card clips whatever
   * grows past its box.
   */
  const props = defineProps<{
    /** The svg loader hands over a component, not a path, so the type stays open. */
    icon: string | object;
    label: string;
    open: boolean;
  }>();

  const emit = defineEmits<{
    'update:open': [open: boolean];
    configure: [];
    'edit-homologation': [];
    delete: [];
  }>();

  const MENU_WIDTH = 229;
  const MENU_HEIGHT = 152;
  const MENU_GAP = 8;
  const VIEWPORT_MARGIN = 8;

  const MENU_CLASS =
    'fixed top-[var(--record-menu-top)] left-[var(--record-menu-left)] z-50 w-[229px] rounded-2xl border border-gray-02 bg-white-01 px-3 py-2 shadow-lg';

  // The icons are used at their native 24, because the svg loader strips the
  // viewBox and a size class would only grow the box and push the glyph off
  // centre. The gap between items is part of the 152 of height the three items add up to.
  const MENU_ITEM_CLASS =
    'record-row-menu-item mt-1 flex w-full items-center gap-3 whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm text-gray-07 first:mt-0 hover:bg-gray-01';

  const triggerRef = ref<HTMLElement | null>(null);
  const menuTop = ref(0);
  const menuLeft = ref(0);

  const menuPosition = computed(() => ({
    '--record-menu-top': `${menuTop.value}px`,
    '--record-menu-left': `${menuLeft.value}px`,
  }));

  const updatePosition = () => {
    const rect = triggerRef.value?.getBoundingClientRect();
    if (!rect) return;

    const fitsBelow = rect.bottom + MENU_GAP + MENU_HEIGHT <= window.innerHeight - VIEWPORT_MARGIN;

    menuTop.value = fitsBelow
      ? rect.bottom + MENU_GAP
      : Math.max(VIEWPORT_MARGIN, rect.top - MENU_GAP - MENU_HEIGHT);
    menuLeft.value = Math.max(VIEWPORT_MARGIN, rect.right - MENU_WIDTH);
  };

  const stopTracking = () => {
    window.removeEventListener('scroll', updatePosition, true);
    window.removeEventListener('resize', updatePosition);
  };

  const startTracking = () => {
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
  };

  const trackPanel = () => {
    updatePosition();
    startTracking();
  };

  watch(
    () => props.open,
    (open) => {
      if (open) {
        trackPanel();
        return;
      }

      stopTracking();
    }
  );

  /**
   * A row can be created already open, when the list re-renders while the menu is
   * up. The watcher cannot answer for that: an immediate run happens while the
   * trigger has no element yet, so there would be nothing to measure and the
   * panel would sit at the origin of the viewport until the next scroll.
   */
  onMounted(() => {
    if (props.open) {
      trackPanel();
    }
  });

  /**
   * A row can be unmounted while its menu is open: a new page, a search, a
   * collapsed expansion. Nothing may survive that, neither the listeners nor the
   * key the listing holds, which would otherwise reopen the panel by itself when
   * a row with the same key comes back.
   */
  onUnmounted(() => {
    stopTracking();

    if (props.open) {
      emit('update:open', false);
    }
  });

  const items = computed<ActionMenuItem[]>(() => [
    { id: 'configure', label: 'Configurar', icon: SettingsIcon, onClick: () => emit('configure') },
    {
      id: 'edit-homologation',
      label: 'Editar Homologação',
      icon: HomologationIcon,
      onClick: () => emit('edit-homologation'),
    },
    { id: 'delete', label: 'Deletar', icon: TrashIcon, onClick: () => emit('delete') },
  ]);
</script>

<template>
  <div
    ref="triggerRef"
    class="flex items-center"
    :style="menuPosition"
  >
    <ActionMenu
      :items="items"
      :model-value="props.open"
      stop-trigger-click-propagation
      :menu-class="MENU_CLASS"
      :item-class="MENU_ITEM_CLASS"
      @update:model-value="emit('update:open', $event)"
    >
      <template #trigger>
        <Tooltip
          :content="props.label"
          trigger="mouseenter focusin"
          placement="top"
          class="flex items-center"
        >
          <button
            type="button"
            :aria-label="props.label"
            aria-haspopup="menu"
            :aria-expanded="props.open"
            data-testid="record-action-more"
            class="flex items-center"
          >
            <IconImage
              :src="props.icon"
              alt=""
            />
          </button>
        </Tooltip>
      </template>
    </ActionMenu>
  </div>
</template>
